import { useRef, useCallback } from "react";
import { toast } from "react-toastify";
import JSZip from "jszip";
import { MAX_PROJECT_SIZE_BYTES, formatBytes } from "../services/projectService";

/**
 * Hook to manage local project file imports (.zip, .py, .js, .sb3 via FileReader/JSZip)
 * and file downloads (multi-file .zip archives, single blobs, and Scratch SB3 iframe messages).
 */
export const useWorkspaceFileIO = ({
  activeWorkspaceEnv,
  setActiveWorkspaceEnv,
  activeWorkspaceCode,
  activeWorkspaceFiles,
  projectName,
  setProjectName,
  setProjectId,
  setLoadedProjectData,
  setActiveWorkspaceCode,
  setActiveWorkspaceFiles,
  setLastSavedCloudCode,
  setBaselineCode,
  setWorkspaceKey,
  isDirty,
  openDialog,
  onClearDraft,
}) => {
  const fileInputRef = useRef(null);

  const executeDownloadLocal = useCallback(
    async (finalName) => {
      const nameToUse = (finalName || projectName || "project").trim();
      setProjectName(nameToUse);

      if (activeWorkspaceEnv === "scratch") {
        // Send message to Scratch iframe to trigger native SB3 download
        const iframe = document.querySelector(
          "iframe[title='Practido Visual Block Studio']"
        );
        if (iframe && iframe.contentWindow) {
          iframe.contentWindow.postMessage(
            { type: "DOWNLOAD_SB3", payload: { projectName: nameToUse } },
            "*"
          );
        }
        return;
      }

      try {
        const zip = new JSZip();
        const safeBaseName = nameToUse.replace(/[^a-z0-9_-]/gi, "_").toLowerCase() || "project";

        // Multi-file packing via JSZip
        if (Array.isArray(activeWorkspaceFiles) && activeWorkspaceFiles.length > 0) {
          activeWorkspaceFiles.forEach((file) => {
            const filePath = file.path || file.name;
            if (file.type === "folder") {
              zip.folder(filePath);
            } else {
              zip.file(filePath, file.content ?? "");
            }
          });
        } else if (activeWorkspaceCode) {
          const entryName = activeWorkspaceEnv === "javascript" ? "main.js" : "main.py";
          zip.file(entryName, typeof activeWorkspaceCode === "string" ? activeWorkspaceCode : JSON.stringify(activeWorkspaceCode));
        }

        const zipBlob = await zip.generateAsync({ type: "blob" });
        const filename = `${safeBaseName}.zip`;

        const url = URL.createObjectURL(zipBlob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        toast.success(`Downloaded project as "${filename}"`);
      } catch (err) {
        console.error("Failed to package .zip project:", err);
        toast.error("Failed to generate project .zip file.");
      }
    },
    [activeWorkspaceCode, activeWorkspaceFiles, activeWorkspaceEnv, projectName, setProjectName]
  );

  const handleDownloadClick = useCallback(() => {
    if (
      projectName.trim().toLowerCase() === "unnamed project" ||
      projectName.trim() === ""
    ) {
      openDialog({
        type: "RENAME",
        title: "Name Your Project",
        message: "",
        confirmText: "Download Project",
        confirmClass: "bg-purple-600 hover:bg-purple-500 text-white",
        cancelText: "Cancel",
        showInput: true,
        inputValue: projectName === "Unnamed Project" ? "" : projectName,
        payload: "download",
      });
    } else {
      executeDownloadLocal(projectName);
    }
  }, [projectName, openDialog, executeDownloadLocal]);

  const handleUploadLocalClick = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, []);

  const executeLoadLocalProject = useCallback(
    (localProject) => {
      if (!localProject) return;
      const { projectName: name, environment: targetEnv, code, files } = localProject;

      if (targetEnv && targetEnv !== activeWorkspaceEnv && setActiveWorkspaceEnv) {
        setActiveWorkspaceEnv(targetEnv);
      }

      setProjectName(name || "Unnamed Project");
      setProjectId(null); // Local import is treated as new cloud project

      if (Array.isArray(files) && files.length > 0) {
        if (setActiveWorkspaceFiles) {
          setActiveWorkspaceFiles(files);
        }
        setLoadedProjectData({ code, files });
      } else {
        if (setActiveWorkspaceFiles) {
          setActiveWorkspaceFiles(null);
        }
        setLoadedProjectData(code);
      }

      setActiveWorkspaceCode(code);
      setLastSavedCloudCode(null);
      setBaselineCode(code);

      if (onClearDraft) {
        onClearDraft();
      }

      // Force remount of modular workspace to trigger initialData load
      setWorkspaceKey((prev) => prev + 1);
      toast.success(`Opened "${name}" successfully`);
    },
    [
      activeWorkspaceEnv,
      setActiveWorkspaceEnv,
      setProjectName,
      setProjectId,
      setLoadedProjectData,
      setActiveWorkspaceCode,
      setActiveWorkspaceFiles,
      setLastSavedCloudCode,
      setBaselineCode,
      setWorkspaceKey,
      onClearDraft,
    ]
  );

  const handleFileChange = useCallback(
    async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;

      if (file.size > MAX_PROJECT_SIZE_BYTES) {
        toast.error(
          `File "${file.name}" (${formatBytes(file.size)}) exceeds the maximum allowed project size limit of ${formatBytes(MAX_PROJECT_SIZE_BYTES)}. Please choose a smaller file.`
        );
        e.target.value = "";
        return;
      }

      const fileName = file.name;
      let newName = fileName.split(".").slice(0, -1).join(".") || fileName;

      const getEnvDisplayName = (env) => {
        if (env === "python") return "Python";
        if (env === "javascript") return "JavaScript";
        if (env === "scratch") return "Scratch Visual Blocks";
        if (env === "junior") return "Scratch Jr";
        return env ? env.toUpperCase() : "Workspace";
      };

      const promptOrLoad = (localProject, targetEnv, displayTargetName) => {
        const currentEnvName = getEnvDisplayName(activeWorkspaceEnv);
        if (targetEnv === activeWorkspaceEnv) {
          const hasContent =
            typeof activeWorkspaceCode === "string"
              ? activeWorkspaceCode.trim().length > 0
              : !!activeWorkspaceCode;

          if (isDirty || hasContent) {
            openDialog({
              type: "LOAD_LOCAL_PROJECT",
              title: `Open "${file.name}"?`,
              message: `Opening this local file will replace your current workspace content. Any unsaved changes will be cleared. Are you sure you want to proceed?`,
              confirmText: "Open File",
              confirmClass: "bg-purple-600 hover:bg-purple-500 text-white",
              cancelText: "Cancel",
              showInput: false,
              payload: localProject,
            });
          } else {
            executeLoadLocalProject(localProject);
          }
        } else {
          openDialog({
            type: "LOAD_LOCAL_PROJECT",
            title: `Switch to ${displayTargetName} & Open File?`,
            message: `You selected a ${displayTargetName} project ("${file.name}"). Opening it will switch your editor from ${currentEnvName} to ${displayTargetName} environment and clear your current work. Are you sure you want to proceed?`,
            confirmText: `Switch to ${displayTargetName} & Open`,
            confirmClass: "bg-purple-600 hover:bg-purple-500 text-white",
            cancelText: "Cancel",
            showInput: false,
            payload: localProject,
          });
        }
      };

      try {
        // CASE 1: .zip archive (Multi-File Practido Project)
        if (fileName.endsWith(".zip")) {
          const zip = await JSZip.loadAsync(file);
          const extractedFiles = [];
          let hasPy = false;
          let hasJs = false;

          const entries = Object.keys(zip.files);
          for (let i = 0; i < entries.length; i++) {
            const relPath = entries[i];
            const zipEntry = zip.files[relPath];

            // Ignore macOS metadata & system files
            if (relPath.startsWith("__MACOSX/") || relPath.includes(".DS_Store")) {
              continue;
            }

            if (zipEntry.dir) {
              // Folder entry
              const folderName = relPath.replace(/\/$/, "").split("/").pop();
              if (folderName) {
                extractedFiles.push({
                  id: `dir_${Date.now()}_${i}`,
                  name: folderName,
                  type: "folder",
                  parentId: null,
                  path: relPath.replace(/\/$/, ""),
                  content: "",
                  isEntry: false,
                });
              }
            } else {
              // File entry
              const textContent = await zipEntry.async("text");
              const leafName = relPath.split("/").pop();

              if (leafName.endsWith(".py")) hasPy = true;
              if (leafName.endsWith(".js")) hasJs = true;

              extractedFiles.push({
                id: `file_${Date.now()}_${i}`,
                name: leafName,
                type: "file",
                parentId: null,
                path: relPath,
                content: textContent,
                isEntry: false,
              });
            }
          }

          if (extractedFiles.length === 0) {
            toast.error("The selected .zip archive contains no readable project files.");
            e.target.value = null;
            return;
          }

          // Determine environment from files
          let detectedEnv = activeWorkspaceEnv;
          if (hasPy && !hasJs) detectedEnv = "python";
          else if (hasJs && !hasPy) detectedEnv = "javascript";
          else if (hasPy) detectedEnv = "python";

          // Designate entry file (main.py or main.js or first code file)
          const targetEntryName = detectedEnv === "javascript" ? "main.js" : "main.py";
          let entryFile = extractedFiles.find(
            (f) => f.type === "file" && f.name.toLowerCase() === targetEntryName
          );
          if (!entryFile) {
            entryFile = extractedFiles.find(
              (f) => f.type === "file" && (f.name.endsWith(".py") || f.name.endsWith(".js"))
            ) || extractedFiles.find((f) => f.type === "file");
          }

          if (entryFile) {
            entryFile.isEntry = true;
          }

          const localProject = {
            projectName: newName,
            environment: detectedEnv,
            files: extractedFiles,
            code: entryFile?.content || "",
          };

          promptOrLoad(localProject, detectedEnv, getEnvDisplayName(detectedEnv));
        }
        // CASE 2: Single .py or .js file
        else if (fileName.endsWith(".py") || fileName.endsWith(".js")) {
          const fileEnv = fileName.endsWith(".js") ? "javascript" : "python";
          const reader = new FileReader();
          reader.onload = (event) => {
            const fileContent = event.target.result || "";
            const defaultEntryName = fileEnv === "javascript" ? "main.js" : "main.py";
            const isEntry = fileName.toLowerCase() === defaultEntryName;

            const singleFile = {
              id: "main",
              name: fileName,
              type: "file",
              parentId: null,
              path: fileName,
              content: String(fileContent),
              isEntry: isEntry || true,
            };

            const localProject = {
              projectName: newName,
              environment: fileEnv,
              code: String(fileContent),
              files: [singleFile],
            };

            promptOrLoad(localProject, fileEnv, getEnvDisplayName(fileEnv));
          };
          reader.readAsText(file);
        }
        // CASE 3: Scratch .sb3 project
        else if (fileName.endsWith(".sb3")) {
          const reader = new FileReader();
          reader.onload = (event) => {
            const localProject = {
              projectName: newName,
              environment: "scratch",
              code: event.target.result,
            };
            promptOrLoad(localProject, "scratch", "Scratch Visual Blocks");
          };
          reader.readAsArrayBuffer(file);
        } else {
          toast.error("Please upload a valid .zip, .py, .js, or .sb3 file.");
        }
      } catch (err) {
        console.error("Error reading uploaded file:", err);
        toast.error("Failed to read the uploaded project file.");
      }

      e.target.value = null; // Reset input for repeated selections
    },
    [
      activeWorkspaceEnv,
      activeWorkspaceCode,
      isDirty,
      openDialog,
      executeLoadLocalProject,
    ]
  );

  return {
    fileInputRef,
    handleUploadLocalClick,
    handleFileChange,
    handleDownloadClick,
    executeDownloadLocal,
    executeLoadLocalProject,
  };
};

export default useWorkspaceFileIO;
